import { openPdf } from './pdfPages';

/**
 * Склейка нескольких документов в один — порт `PdfMerger` Android-версии.
 *
 * ## Почему через растр
 *
 * Страницы пересобираются из картинок, и текстовый слой в них теряется. Терять почти
 * нечего: сканы и снимки текста не содержат изначально. Он есть только у принесённого
 * «родного» PDF — происхождение мы знаем (`DocumentSource.Imported`) и предупреждаем
 * юриста заранее ([D-41](docs/DECISIONS.md)).
 *
 * Взять готовые страницы как есть pdf-lib умеет (`copyPages`), и для двух наших PDF
 * это было бы лучше. Но тогда склейка работала бы **по-разному** для разных пар
 * документов: снимок и PDF так не совместить, а объяснять юристу, почему «объединить»
 * иногда сохраняет текст, а иногда нет, — хуже, чем один предсказуемый способ.
 *
 * ## Что здесь важно
 *
 * Страницы рисуются по одной и сразу отпускаются: том дела на триста листов нельзя
 * держать в памяти целиком ([NFR.md](docs/NFR.md)).
 */

/** Ширина страницы в точках при пересборке: мелкий шрифт скана остаётся читаемым. */
const PAGE_WIDTH = 1400;

const JPEG_QUALITY = 0.85;

/** Меньше двух документов объединять бессмысленно. */
const MIN_PARTS = 2;

export interface MergedPdf {
  content: Blob;
  pageCount: number;
}

/** Части склейки: файл и его тип — от типа зависит, чем его читать. */
export interface MergePart {
  content: Blob;
  mimeType: string;
}

export async function mergeDocuments(parts: MergePart[]): Promise<MergedPdf | null> {
  if (parts.length < MIN_PARTS) return null;

  const { PDFDocument } = await import('pdf-lib');
  const merged = await PDFDocument.create();
  const canvas = document.createElement('canvas');

  for (const part of parts) {
    const pages =
      part.mimeType === 'application/pdf'
        ? await pdfPagesOf(part.content, canvas)
        : await imagePageOf(part.content, canvas);

    for (const page of pages) {
      const embedded = await merged.embedJpg(page.bytes);
      merged.addPage([page.width, page.height]).drawImage(embedded, {
        x: 0,
        y: 0,
        width: page.width,
        height: page.height,
      });
    }
  }

  if (merged.getPageCount() === 0) return null;

  const bytes = await merged.save();
  return {
    content: new Blob([new Uint8Array(bytes).buffer], { type: 'application/pdf' }),
    pageCount: merged.getPageCount(),
  };
}

interface RasterPage {
  bytes: ArrayBuffer;
  width: number;
  height: number;
}

async function pdfPagesOf(content: Blob, canvas: HTMLCanvasElement): Promise<RasterPage[]> {
  const pdf = await openPdf(content);
  if (!pdf) return [];

  const pages: RasterPage[] = [];
  for (let number = 1; number <= pdf.pageCount; number += 1) {
    const size = await pdf.render(number, canvas, PAGE_WIDTH);
    if (!size) continue;

    const jpeg = await toJpeg(canvas);
    if (jpeg) pages.push({ bytes: jpeg, width: size.width, height: size.height });
  }

  await pdf.close();
  return pages;
}

async function imagePageOf(content: Blob, canvas: HTMLCanvasElement): Promise<RasterPage[]> {
  const bitmap = await createImageBitmap(content).catch(() => null);
  if (!bitmap) return [];

  const scale = PAGE_WIDTH / bitmap.width;
  const width = PAGE_WIDTH;
  const height = Math.round(bitmap.height * scale);

  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext('2d');
  if (!context) {
    bitmap.close();
    return [];
  }

  context.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  const jpeg = await toJpeg(canvas);
  return jpeg ? [{ bytes: jpeg, width, height }] : [];
}

async function toJpeg(canvas: HTMLCanvasElement): Promise<ArrayBuffer | null> {
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY),
  );
  return blob ? blob.arrayBuffer() : null;
}
